import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { Button } from '../ui'
import {
  confirmConsent,
  declineConsent,
  getStagedConsent,
  type ConsentStatus,
  type StagedConsent,
} from '../lib/api'
import './magic-link.css'

/**
 * Where a customer decides whether to attach the directory that just consented.
 *
 * This screen is a security control, not a courtesy. Microsoft's admin-consent redirect is
 * unsigned and unauthenticated by protocol, and the cookie meant to make a captured state
 * insufficient is sent automatically on exactly the top-level navigation an attacker can cause —
 * so a callback can be forged, and the tenant it names can be the attacker's. What a forged
 * callback CANNOT do is produce the POST this page makes: a navigation cannot carry a same-site
 * form submission with the session behind it.
 *
 * So the callback stages a claim, and this is where a person looks at it and decides.
 */
export function ConsentReview() {
  const navigate = useNavigate()

  const [staged, setStaged] = useState<StagedConsent | null>(null)
  const [loading, setLoading] = useState(true)
  // Why nothing was loaded: 404 is "used or expired"; anything else is not, and must not say so.
  const [loadStatus, setLoadStatus] = useState<number | null>(null)
  const [attempt, setAttempt] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // A refusal that retrying cannot fix. Separate from `error`, because most errors here ARE worth
  // retrying -- a network blip, a staged consent that expired -- and the button should come back
  // for those.
  const [settled, setSettled] = useState(false)

  useEffect(() => {
    let cancelled = false

    void (async () => {
      const result = await getStagedConsent()
      if (cancelled) return

      // 404 is the ordinary case for a page opened twice or left past its fifteen minutes, not an
      // error worth alarming anyone about. Every OTHER failure used to be read the same way, so an
      // administrator who had just approved in Microsoft and hit a network blip was told the
      // request had expired -- and redid admin consent for nothing. The stage lasts fifteen
      // minutes; a retry is the honest answer to a blip.
      setStaged(result.ok ? result.data : null)
      setLoadStatus(result.ok ? null : result.status)
      setLoading(false)
    })()

    return () => {
      cancelled = true
    }
  }, [attempt])

  async function onConfirm() {
    if (!staged || busy) return

    setBusy(true)
    setError(null)

    // The tenant that was RENDERED, not whatever is staged now. Staging deliberately does not
    // consume the request, so this is what stops a swap between this page loading and the button
    // being pressed.
    const result = await confirmConsent(staged.tenantId)

    if (!result.ok) {
      // 409 now means one thing only: that directory belongs to a DIFFERENT workspace. It used
      // to also cover "you already connected this", so reloading this page told a customer their
      // own tenant belonged to somebody else.
      setError(
        // WHAT A PERSON CAN ACTUALLY DO NEXT.
        //
        // This said "disconnect it there first", which is advice nobody can follow: the reader
        // does not know who "there" is, and the product will not tell them. It is a dead end
        // dressed as an instruction, and it has already stopped a real signup -- somebody
        // connected their employer's tenant, was refused, and has an empty account to show for it.
        //
        // Deliberately does NOT name the workspace, its owner, or confirm anything about them.
        // Whoever reads this has proved they can grant tenant-wide consent, which is not nothing,
        // but it is not the same as being entitled to know who runs that organisation's security
        // reporting. Several Entra roles can grant that consent, including ones a contractor or a
        // guest might hold. The colleague they are told to ask is somebody they already know if
        // the claim is true, and nobody the product has identified if it is not.
        //
        // Asking to join, and an owner approving, is the real fix. It needs its own table, a
        // narrow privileged write, approval rules, and a way to belong to more than one
        // workspace -- none of which is a sentence. Until then this at least points somewhere.
        result.status === 409
          ? 'Someone at your organisation has already connected this directory to Defenssive. ' +
            'Ask them to add you to it — one account per organisation is deliberate, so that ' +
            'everyone sees the same findings.'
          : 'That did not work. Start the connection again from the beginning.',
      )

      // 409 IS FINAL FOR THIS WORKSPACE. Pressing the button again asks the same question and
      // gets the same answer, every time, for as long as the other workspace holds that
      // directory. Leaving it enabled invites exactly that: a person clicks it three or four
      // times, watches nothing change, and concludes the product is broken rather than that it is
      // protecting somebody else's tenant. It also spends the rate limit that exists to stop a
      // stranger probing which directories are already connected.
      //
      // The message already says what to do -- disconnect it there first -- and that is not
      // something this screen can do for them.
      if (result.status === 409) setSettled(true)

      setBusy(false)
      return
    }

    const outcome = outcomeFor(result.data.status)

    // Connected, or attached and awaiting Microsoft's confirmation. Either way the tenant is
    // recorded and the next useful screen is the one showing what we found -- not the screen that
    // asks you to connect a tenant, which is where this used to send people after they just had.
    //
    // `pending` goes there too, deliberately: the dashboard renders freshness before anything
    // derived from it, so "attached, nothing read yet" is a state it can state honestly. Bouncing
    // someone back to Connect would imply the connection did not happen.
    if (outcome === 'connected' || outcome === 'pending') {
      navigate('/dashboard', { replace: true })
      return
    }

    navigate(`/connect?outcome=${outcome}`, { replace: true })
  }

  async function onDecline() {
    if (busy) return

    setBusy(true)
    await declineConsent()
    // After a final refusal this is "Back", not a decline: the person chose to connect and was
    // refused. Reporting "You declined that connection" and inviting them to start again sent them
    // round to the same refusal. The stage is still cleared -- nothing here can use it.
    navigate(settled ? '/connect?outcome=connected_elsewhere' : '/connect?outcome=declined', { replace: true })
  }

  if (loading) {
    return (
      <main className="magic-link">
        <div className="label-mono">Defenssive AI Security Manager</div>
        <p className="text-muted">Checking what is waiting…</p>
      </main>
    )
  }

  if (!staged && loadStatus === 401) {
    return (
      <main className="magic-link rise-in">
        <div className="label-mono">Defenssive AI Security Manager</div>
        <h2>Sign in to continue</h2>
        <p className="text-muted">
          Your sign-in has ended, so this page cannot show what is waiting. Sign in again, then
          start the connection again. Your approval in Microsoft is not affected.
        </p>
        <Button variant="primary" onClick={() => navigate('/auth/sign-in')}>
          Sign in
        </Button>
      </main>
    )
  }

  if (!staged && loadStatus !== 404) {
    return (
      <main className="magic-link rise-in">
        <div className="label-mono">Defenssive AI Security Manager</div>
        <h2>We could not load this</h2>
        <p className="text-muted" role="alert">
          We could not load what is waiting to be confirmed. If you approved access in Microsoft,
          that approval stands — try again in a moment. Requests last fifteen minutes.
        </p>
        <Button
          variant="primary"
          onClick={() => {
            setLoading(true)
            setAttempt((n) => n + 1)
          }}
        >
          Try again
        </Button>
      </main>
    )
  }

  if (!staged) {
    return (
      <main className="magic-link rise-in">
        <div className="label-mono">Defenssive AI Security Manager</div>
        <h2>Nothing to confirm</h2>
        <p className="text-muted">
          This connection request has already been used, or it expired. Requests last fifteen
          minutes.
        </p>
        <Button variant="primary" onClick={() => navigate('/connect')}>
          Start again
        </Button>
      </main>
    )
  }

  return (
    <main className="magic-link rise-in">
      <div className="label-mono">Defenssive AI Security Manager</div>

      <h2>Confirm this directory</h2>

      {/* It said "approved read-only access". The Exchange permission on that consent screen is not
          read-only by name -- the privacy notice says so in as many words -- so what was approved is
          named for what it is, Defenssive's access, and the read-only promise stays with what
          Defenssive does (the Connect screen), not with what Microsoft granted. */}
      <p>
        An administrator approved Defenssive&rsquo;s access in Microsoft. <strong>Nothing is connected
        yet.</strong> Check that this is your organisation&rsquo;s directory before continuing.
      </p>

      {/* The raw id, deliberately, and deliberately not dressed up as something friendlier. It
          came out of a query string on an unsigned redirect, so anything shown beside it — a name,
          a domain — would have to be fetched using the very access this screen exists to
          authorise, and would be the claim vouching for itself. Showing a directory NAME is worth
          doing and the honest place is after verification, on the next screen. In PLAN.md. */}
      <p className="tnum mono consent-tenant">{staged.tenantId}</p>

      <p className="footnote text-muted">
        Find this in the Microsoft Entra admin centre under Overview → Tenant ID. If it does not
        match, do not continue.
      </p>

      {error ? (
        <div className="sign-in-notice" role="alert">
          {error}
        </div>
      ) : null}

      <div className="consent-actions">
        {/*
          Hidden rather than disabled once the answer is final. A greyed-out button still reads as
          "the way forward, currently unavailable", and there is no forward from here -- the way
          out is Cancel, which is why it stops being the secondary action.
        */}
        {settled ? null : (
          <Button variant="primary" onClick={onConfirm} disabled={busy}>
            {busy ? 'Connecting…' : 'Yes, connect this directory'}
          </Button>
        )}

        <Button variant={settled ? 'primary' : undefined} onClick={onDecline} disabled={busy}>
          {settled ? 'Back' : 'Cancel'}
        </Button>
      </div>
    </main>
  )
}

/**
 * The server's verdict, in the words the next screen uses.
 *
 * `pending_verification` is neither a failure nor a success: the tenant is attached and cannot be
 * read from until Microsoft confirms it. Collapsing it into either would be the kind of small lie
 * this product cannot afford — the customer told they are connected while nothing scans.
 */
function outcomeFor(status: ConsentStatus): string {
  switch (status) {
    case 'verified':
      return 'connected'
    case 'pending_verification':
      return 'pending'
    // Reached only via a 200, and only when the tenant is connected to THIS workspace. The
    // blocked case never gets here -- it is a 409 handled above, with its own message.
    case 'connected_elsewhere':
      return 'connected_elsewhere'

    case 'tenant_mismatch':
      return 'tenant_mismatch'

    // Already attached, already verified, nothing waiting. The API used to report this as
    // `nothing_pending` and this switch had no case for it, so it fell through to `refused` --
    // and a customer who reconnected an already-connected tenant was told the connection had
    // failed, having just watched it succeed.
    //
    // Kept as a case even though the API no longer sends it, because an older API and a newer
    // page will meet during a deploy, and this is the pairing that produced the bug.
    case 'nothing_pending':
      return 'connected'

    case 'refused':
      return 'refused'

    default:
      // EXHAUSTIVE, and this is the line that would have prevented the bug.
      //
      // `nothing_pending` was already in the ConsentStatus union. The switch simply had no case
      // for it, and `default: return 'refused'` swallowed it -- so a successful reconnection was
      // reported as a failed one, and the compiler had every piece of information needed to say
      // so and was never asked.
      //
      // Now a status added to the union without a case here is a build error. Not `refused`,
      // which was the third time in this flow that "everything else is an error" turned a
      // successful outcome into a failure message: first on the connect screen, then on the
      // callback, then here.
      return assertNever(status)
  }
}

/**
 * Fails the build when a ConsentStatus is added without being handled.
 *
 * At runtime it returns a value that is honest rather than reassuring: an unrecognised status
 * means this page is older than the API it is talking to, which is a deploy in progress and not
 * something to accuse the customer's tenant of.
 */
function assertNever(status: never): string {
  return `unrecognised:${String(status)}`
}
